import { useEffect, useState, useRef } from "react";
import { Box, Dialog, styled, IconButton, Typography, Slider, Fade } from "@mui/material";
import { IRoom } from "../../models/stat";
import Progress from "../../components/progress/Progress";
import SearchBar from "../../components/searchbar/SearchBar";
import FloorList from "./FloorList";
import LocalDialog from "./LocalDialog";
import RoomList from "./RoomList";
import "../../styles/cursor.css";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";
import { InteractiveStatMap } from "../../components/plan/StatMap/interactiveStatMap";
import { canEdit } from "../../utils/permissions";
import useMapScansFloors from "./useMapScansFloors";

export const getRoomMinutes = (room: IRoom) =>
    room.scans.reduce((acc, s) => (acc += s.minutes), 0);

const ToolBox = styled(Box)(({ theme }) => ({
    marginBottom: "16px",
    padding: "8px",
    display: "flex",
    justifyContent: "right",
    backgroundColor: theme.palette.background.paper,
}));

const Tool = styled(IconButton)(({ theme }) => ({
    color: "black",
    "&:hover": {
        color: theme.palette.primary.main,
    },
}));

const HBscanPage = () => {
    const { t } = useTranslation();

    const {
        start,
        setStart,
        end,
        setEnd,
        hbScanFloors,
        filteredHBScanFloors,
        buildingId,
        setBuildingId,
        buildings,
        floorId,
        setFloorId,
        employees,
        selectedEmployees,
        setSelectedEmployees,
        isLoading,
        selectedLocalId,
        setSelectedLocalId,
        isEditPopup,
        setIsEditPopup,
        map,
        ref,
        circleRadius,
        onCricleRadiusChange,
        routes,
        selectedRoutes,
        setSelectedRoutes
    } = useMapScansFloors();

    const leftBoxRef = useRef<HTMLDivElement>(null);
    const [mapHeight, setMapHeight] = useState(0);
    const [isShowSlider, setIsShowSlider] = useState(false);

    useEffect(() => {
        if (!leftBoxRef.current) return;

        const updateHeight = () => {
            setMapHeight(leftBoxRef.current?.offsetHeight || 0);
        };

        let resizeObserver: ResizeObserver;
        if (ResizeObserver) {
            resizeObserver = new ResizeObserver(() => {
                updateHeight();
            });
            resizeObserver.observe(leftBoxRef.current);
        } else {
            window.addEventListener("resize", updateHeight);
        }

        updateHeight();

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            } else {
                window.removeEventListener("resize", updateHeight);
            }
        };
    }, [leftBoxRef.current]);

    const changeFloor = (id: number) => setFloorId(id);

    const setRoomId = (id: number) =>
        map?.setRoomId(id).then(() => setIsEditPopup(false));

    return (
        <>
            <SearchBar
                buildings={buildings}
                setBuildingId={setBuildingId}
                buildingId={buildingId}
                floors={hbScanFloors}
                floorIndex={floorId}
                setFloorIndex={changeFloor}
                employees={employees}
                selectedEmployees={selectedEmployees}
                setSelectedEmployees={setSelectedEmployees}
                start={start}
                setStart={setStart}
                end={end}
                setEnd={setEnd}
                selectedRoutes={selectedRoutes}
                setSelectedRoutes={setSelectedRoutes}
                routes={routes}
            />
            {floorId <= 0 && !isLoading ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: 4,
                    }}
                >
                    <Typography variant="h5">{t("header.buildingHasNoFloorsWithMap")}</Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: 4,
                        gap: { xs: 2, md: 4 },
                        flexDirection: { xs: "column", md: "row" },
                    }}
                >
                    <Box
                        ref={leftBoxRef}
                        sx={{
                            width: "100%",
                        }}
                    >
                        {canEdit() && (
                            <ToolBox>
                                {/* Using Ids to be able to assing, to each button, an onClick function to change strategy. This is done in interactiveStatMap.ts  */}
                                <Fade in={isShowSlider}>
                                    <Box width="15vw" minWidth='115px' display="flex" alignContent='center' alignItems='center'>
                                        <Slider aria-label="circle-radius" min={InteractiveStatMap.MIN_RADIUS} max={InteractiveStatMap.MAX_RADIUS} defaultValue={InteractiveStatMap.DEFAULT_RADIUS} value={circleRadius} onChange={(_, value) => { onCricleRadiusChange(value as number) }} />
                                    </Box>
                                </Fade>
                                <Tool id="addButton" onClick={() => { setIsShowSlider((old) => !old) }}>
                                    <EditIcon fontSize="large"></EditIcon>
                                </Tool>
                                <Tool id="deleteButton" onClick={() => { setIsShowSlider(false) }}>
                                    <DeleteIcon fontSize="large"></DeleteIcon>
                                </Tool>
                                <Tool id="saveButton" onClick={() => { setIsShowSlider(false) }}>
                                    <SaveIcon fontSize="large"></SaveIcon>
                                </Tool>
                            </ToolBox>
                        )}

                        <Box
                            ref={ref}
                            sx={{
                                width: "100%",
                                paddingInline: 2,
                                backgroundColor: (theme) =>
                                    theme.palette.background.paper,
                            }}
                        />
                    </Box>

                    <FloorList
                        maxHeight={leftBoxRef.current?.clientHeight || 400}
                        height={mapHeight}
                        minWidth={350}
                        hbscanFloors={filteredHBScanFloors}
                        changeFloor={changeFloor}
                        floorIndex={floorId}
                        buildingId={buildingId}
                    />
                </Box>
            )}
            <LocalDialog
                selectedLocalId={selectedLocalId}
                setSelectedLocalId={setSelectedLocalId}
                hbScanFloors={filteredHBScanFloors}
            />
            <Dialog open={isEditPopup} onClose={() => setIsEditPopup(false)}>
                <RoomList
                    onRoomSelect={setRoomId}
                    floorId={floorId}
                    buildingId={buildingId}
                ></RoomList>
            </Dialog>
        </>
    );
};

export default HBscanPage;
